import { lazy, Suspense } from "react";

const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";

const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Eagerly load the landing page for fast initial render
import Index from "./pages/Index";

// Lazy-load all other routes to reduce initial JS bundle
const Explore = lazy(() => import("./pages/Explore"));
const Editor = lazy(() => import("./pages/Editor"));
const Profile = lazy(() => import("./pages/Profile"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const Auth = lazy(() => import("./pages/Auth"));
const SkinDetail = lazy(() => import("./pages/SkinDetail"));
const Compare = lazy(() => import("./pages/Compare"));
const Feed = lazy(() => import("./pages/Feed"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Capes = lazy(() => import("./pages/Capes"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Admin = lazy(() => import("./pages/Admin"));
const Servers = lazy(() => import("./pages/Servers"));
const ServerDetail = lazy(() => import("./pages/ServerDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const HowTo = lazy(() => import("./pages/HowTo"));
const DMCA = lazy(() => import("./pages/DMCA"));
const Articles = lazy(() => import("./pages/Articles"));
const HowToChangeSkin = lazy(() => import("./pages/HowToChangeSkin"));
const AIGenerator = lazy(() => import("./pages/AIGenerator"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const EmeraldRank = lazy(() => import("./pages/EmeraldRank"));

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}><Toaster /></Suspense>
        <Suspense fallback={null}><Sonner /></Suspense>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/editor" element={<Editor />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/skin/:id" element={<SkinDetail />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/feed" element={<Feed />} />
                <Route path="/creator/:userId" element={<CreatorProfile />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/capes" element={<Capes />} />
                <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/servers" element={<Servers />} />
                <Route path="/server/:id" element={<ServerDetail />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/how-to" element={<HowTo />} />
                <Route path="/dmca" element={<DMCA />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/how-to-change-minecraft-skin" element={<HowToChangeSkin />} />
                <Route path="/ai-generator" element={<AIGenerator />} />
                <Route path="/emerald-rank" element={<EmeraldRank />} />
                <Route path="/:slug" element={<CategoryPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
          <Suspense fallback={null}><CookieConsent /></Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
